import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  TextField,
  Grid2 as Grid,
  FormGroup,
  Grid2Props,
  Autocomplete,
} from "@mui/material";
import { singleGridItem } from "../defaultStyles";
import FormLabel from "../../common/base/FormLabel";
import UserAttributeMetadataPaper from "../../attributeMetadata/UserAttributeMetadataPaper";
import { useInputFullName } from "../subForms/NamePrefixContext";
import { useFormHandlerContext } from "../../../../controllers/formController";

export interface FormMultiTextFieldProps extends Grid2Props {
  name?: string;
  label: string;
  labelActions?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  validate?: (value: any) => string | boolean;
}

const FormMultiTextField: React.FC<FormMultiTextFieldProps> = ({
  name,
  label,
  labelActions,
  required = false,
  disabled = false,
  placeholder,
  validate,
  ...grid
}) => {
  const { isProcessing } = useFormHandlerContext();
  const { control } = useFormContext();
  const fullName = useInputFullName(name);

  const toStringArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.map((x) => (x == null ? "" : String(x))) : [];

  const hasLeadingEmpty = (arr: string[]) => arr.length > 0 && arr[0] === "";

  const stripLeadingEmptyForUi = (arr: string[]) =>
    hasLeadingEmpty(arr) ? arr.slice(1) : arr;

  const restoreLeadingEmptyFromUi = (
    uiArr: string[],
    keepLeadingEmpty: boolean
  ) => {
    const cleaned = uiArr
      .map((x) => (x == null ? "" : String(x)))
      .filter((x) => x.trim() !== ""); // avoid adding blank items by accident

    return keepLeadingEmpty ? ["", ...cleaned] : cleaned;
  };

  const countNonEmpty = (arr: unknown) =>
    toStringArray(arr).filter((x) => x.trim() !== "").length;

  return (
    <Grid {...singleGridItem} {...grid}>
      <UserAttributeMetadataPaper name={fullName}>
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: required ? `${label} is required` : false,
            validate: required
              ? (value) => {
                  // ignore empty strings (including the leading sentinel "")
                  if (countNonEmpty(value) === 0) {
                    return `${label} must contain at least one item`;
                  }
                  const invalid = validate ? validate(value) : false;
                  return invalid || true;
                }
              : validate,
          }}
          render={({ fieldState, field }) => {
            const raw = toStringArray(field.value);
            const keepLeadingEmpty = hasLeadingEmpty(raw);
            const uiValue = stripLeadingEmptyForUi(raw);
            return (
              <FormGroup>
                <FormLabel
                  required={required}
                  name={fullName}
                  label={label}
                  actions={labelActions}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  disabled={disabled || isProcessing}
                  options={[]}
                  value={uiValue}
                  onChange={(_, newValues) => {
                    // keep the empty string in the stored value if it existed originally
                    field.onChange(
                      restoreLeadingEmptyFromUi(
                        newValues as string[],
                        keepLeadingEmpty
                      )
                    );
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        placeholder={placeholder}
                        required={required}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        fullWidth
                      />
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                      minWidth: 200,
                    },
                  }}
                />
              </FormGroup>
            );
          }}
        />
      </UserAttributeMetadataPaper>
    </Grid>
  );
};

export default FormMultiTextField;
